'use client';

import * as React from 'react';
import { ChevronDown, Filter, ChevronRight } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmailCampaignsTable } from './email-campaigns-table';
import { SmsCampaignsTable } from './sms-campaigns-table';
import { PushCampaignsTable } from './push-campaigns-table';
import { cn } from '@/lib/utils';

const timeRanges = [
  { label: 'Last 7 days', value: '7days' },
  { label: 'Last 30 days', value: '30days' },
  { label: 'Last 90 days', value: '90days' },
  { label: 'Custom', value: 'custom' },
];

export function CampaignCommandCenter() {
  const [timeRange, setTimeRange] = React.useState('30days');
  const [activeTab, setActiveTab] = React.useState('email');

  // This would typically come from an API
  const hasData = {
    email: true,
    sms: true,
    push: true,
  };

  return (
    <Card className='w-full overflow-hidden shadow-none'>
      <CardHeader className='flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 border-b'>
        <div>
          <CardTitle className='text-xl font-bold'>
            Campaign Command Center
          </CardTitle>
          <CardDescription className='text-md'>
            Track and analyze your marketing campaign performance
          </CardDescription>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='outline' className='h-9 sm:h-10 gap-1 sm:gap-2'>
              <Filter className='h-3.5 w-3.5 sm:h-4 sm:w-4' />
              <span>{timeRanges.find(r => r.value === timeRange)?.label}</span>
              <ChevronDown className='h-3.5 w-3.5 sm:h-4 sm:w-4 opacity-50' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-[180px]'>
            {timeRanges.map(range => (
              <DropdownMenuItem
                key={range.value}
                className={cn(
                  'cursor-pointer',
                  timeRange === range.value && 'font-medium bg-muted',
                )}
                onClick={() => setTimeRange(range.value)}
              >
                {range.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className='p-0'>
        <Tabs
          defaultValue='email'
          value={activeTab}
          onValueChange={setActiveTab}
          className='w-full'
        >
          <div className='border-b overflow-x-auto scrollbar-hide'>
            <div className='px-4 sm:px-6'>
              <TabsList className='h-12 w-full justify-start gap-0 bg-transparent p-0'>
                <TabsTrigger
                  value='email'
                  className='h-12 rounded-none text-md border-b-2 border-transparent px-4 data-[state=active]:border-secondary data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                >
                  Email Campaigns
                </TabsTrigger>
                <TabsTrigger
                  value='sms'
                  className='h-12 rounded-none text-md border-b-2 border-transparent px-4 data-[state=active]:border-secondary data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                >
                  SMS Campaigns
                </TabsTrigger>
                <TabsTrigger
                  value='push'
                  className='h-12 rounded-none text-md border-b-2 border-transparent px-4 data-[state=active]:border-secondary data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                >
                  Push Campaigns
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          <TabsContent
            value='email'
            className='mt-0 focus-visible:outline-none focus-visible:ring-0'
          >
            {hasData.email ? (
              <EmailCampaignsTable timeRange={timeRange} />
            ) : (
              <EmptyState message='No email campaigns yet' />
            )}
          </TabsContent>

          <TabsContent
            value='sms'
            className='mt-0 focus-visible:outline-none focus-visible:ring-0'
          >
            {hasData.sms ? (
              <SmsCampaignsTable timeRange={timeRange} />
            ) : (
              <EmptyState message='No SMS campaigns yet' />
            )}
          </TabsContent>

          <TabsContent
            value='push'
            className='mt-0 focus-visible:outline-none focus-visible:ring-0'
          >
            {hasData.push ? (
              <PushCampaignsTable timeRange={timeRange} />
            ) : (
              <EmptyState message='No push campaigns yet' />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className='flex justify-end py-4 px-6 border-t'>
        <Button
          variant='link'
          className='text-md font-medium text-secondary p-0 h-auto flex items-center'
        >
          Create a new campaign
          <ChevronRight className='h-4 w-4 ml-1' />
        </Button>
      </CardFooter>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className='flex flex-col items-center justify-center py-12 px-4 text-center'>
      <div className='rounded-full bg-muted p-3 mb-4'>
        <Filter className='h-6 w-6 text-muted-foreground' />
      </div>
      <h3 className='text-lg font-medium'>{message}</h3>
      <p className='text-md text-muted-foreground mt-1 mb-6'>
        Create your first campaign to see performance data
      </p>
      <Button>Create Campaign</Button>
    </div>
  );
}
